import React, { useState, useEffect, lazy } from "react";
import PageSeo from "../../components/common/PageSeo";
import JsonLd from "../../components/common/JsonLd";
import {
  buildBreadcrumbJsonLd,
  buildProductJsonLd,
} from "../../utils/structuredData";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import styles from "../../styles/pages/product/ProductDetailsPage.module.css";
import ProductDetailsPageSkeleton from "../../components/product/ProductDetailsPageSkeleton";
import PageStatusPanel from "../../components/common/PageStatusPanel";
import ProductPostsSection from "../../components/product/ProductPostsSection";
import ProductDetailsPostCard from "../../components/product/ProductDetailsPostCard";
import ReviewRatingBreakdown from "../../components/product/ReviewRatingBreakdown";
import ProductActionTools from "../../components/product/ProductActionTools";
import ProductImageGallery from "../../components/product/ProductImageGallery";
import ProductMatchInsights from "../../components/product/ProductMatchInsights";
import ProductAiVerdictCard from "../../components/product/ProductAiVerdictCard";
import ProductAiOnboardingCta from "../../components/product/ProductAiOnboardingCta";
import ProductAskChat from "../../components/product/ProductAskChat";
import ProductReviewDigest from "../../components/product/ProductReviewDigest";
import ProductImageLightbox from "../../components/product/ProductImageLightbox";
import ProductReviewsModal from "../../components/product/ProductReviewsModal";
import ProductReviewsSummaryHeader from "../../components/product/ProductReviewsSummaryHeader";
import ProductReviewFormModal from "../../components/product/ProductReviewFormModal";
import ProductCommunityOutcomes from "../../components/product/ProductCommunityOutcomes";
import useCommunitySummary from "../../hooks/useCommunitySummary";
import ProductNearbyStores from "../../components/product/ProductNearbyStores";
import ProductBuyCta from "../../components/product/ProductBuyCta";
import ProductRetailerOffers, {
  visibleRetailerOffers,
} from "../../components/product/ProductRetailerOffers";
import AdSlot from "../../components/common/AdSlot";
import ProductSimilarProductsSection from "../../components/product/ProductSimilarProductsSection";
import AppFeedbackPrompt from "../../components/feedback/AppFeedbackPrompt";
import ProductReviewsListSection from "../../components/product/ProductReviewsListSection";
import { readHomeChatMatchSessionId } from "../../utils/homeChatMatchPersistence";
import { useToast } from "../../contexts/ToastContext";
import { useUserAuth } from "../../contexts/UserAuthContext";
import ConfirmModal from "../../components/common/ConfirmModal";
import {
  FaBookmark,
  FaIndustry,
  FaMicrochip,
  FaCalendar,
  FaTag,
} from "react-icons/fa";
import AddToSetupModal from "../../components/setup/AddToSetupModal";
import { useComparison } from "../../contexts/ComparisonContext";
import capitalizeFirst from "../../utils/capitalizeFirst";
import { useInteractionTracker } from "../../hooks/useRecommendations";
import useProductDetails from "./hooks/useProductDetails";
import useProductReviews from "./hooks/useProductReviews";
import useProductGallery from "./hooks/useProductGallery";
import useProductComparisonActions from "./hooks/useProductComparisonActions";
import useProductInsights from "./hooks/useProductInsights";
import useProductContextData from "./hooks/useProductContextData";
import useSimilarProducts from "./hooks/useSimilarProducts";
import useHomeChatMatchSession from "../../hooks/useHomeChatMatchSession";
import useProductReviewDigest from "../../hooks/useProductReviewDigest";
import useProductInteractionTracking from "./hooks/useProductInteractionTracking";
import useProductPageScrollEffects from "./hooks/useProductPageScrollEffects";
import useProductFavoriteAction from "./hooks/useProductFavoriteAction";
import ProductDeeperInsightsSection from "./sections/ProductDeeperInsightsSection";
import { getCurrentProductAiRank } from "../../utils/resolveSimilarProducts";
import { useRegion } from "../../contexts/RegionContext";
import { formatDisplayPrice } from "../../utils/formatPrice";
import {
  ICONS,
  getDimensionBarClass,
  capitalizeLeadingLetter,
  renderSpecIcon,
  renderSpecValue,
} from "./helpers/productDetailViewHelpers";

// Marketplace cross-link
import MarketplaceListingsSection from "../../components/marketplace/MarketplaceListingsSection";

const MatchVisualization = lazy(() => import("../../components/product/MatchVisualization"));
const DecisionConfidence = lazy(() => import("../../components/product/DecisionConfidence"));
const BudgetAnalysis = lazy(() => import("../../components/product/BudgetAnalysis"));
const ProductDetailsPage = () => {
  const { id, categoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addProduct, selectedProducts } = useComparison();
  const { region } = useRegion();

  // Use extracted hook for core product data
  const {
    product,
    similar,
    loading,
    error,
    categoryFields,
    refetch,
  } = useProductDetails(id);

  const [showCount, setShowCount] = useState(4);
  const [deeperInsightsExpanded, setDeeperInsightsExpanded] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [chatPrefill, setChatPrefill] = useState(null);

  const { showToast } = useToast();
  const { user, setUser } = useUserAuth();

  // Phase 2: Interaction tracking for collaborative filtering
  const { trackView, trackFavorite, trackReview } = useInteractionTracker();

  const {
    selectedImageIndex,
    setSelectedImageIndex,
    lightboxOpen,
    setLightboxOpen,
    imageZoom,
    setImageZoom,
    zoomPosition,
    handleImageMouseMove,
    handlePreviousImage,
    handleNextImage,
  } = useProductGallery(product?.images || []);

  const refreshProductDetails = async () => {
    if (!product?._id) return;
    try {
      await refetch();
    } catch (err) {
      console.error("Failed to refresh product details:", err);
    }
  };

  const notifyProductRatingUpdate = (productId) => {
    if (!productId) return;
    window.dispatchEvent(
      new CustomEvent("productRatingUpdated", { detail: { productId } })
    );
  };

  const {
    reviews,
    averageRating,
    ratingDistribution,
    showReviewModal,
    setShowReviewModal,
    reviewForm,
    setReviewForm,
    reviewFormConfig,
    totalReviews,
    handleSubmitReview,
    editingReview,
    setEditingReview,
    editReviewForm,
    setEditReviewForm,
    handleEditReview,
    startEditReview,
    cancelEditReview,
    isUserReview,
    handleReviewHelpful,
    showDeleteReviewModal,
    requestDeleteReview,
    cancelDeleteReview,
    confirmDeleteReview,
  } = useProductReviews({
    productId: product?._id,
    user,
    setUser,
    showToast,
    navigate,
    location,
    trackReview,
    onReviewsChanged: async () => {
      await refreshProductDetails();
      notifyProductRatingUpdate(product?._id);
    },
  });
  const userReview = reviews.find(isUserReview) || null;

  useProductPageScrollEffects({
    location,
    reviews,
    setEditingReview,
    setEditReviewForm,
    loading,
    product,
  });

  const aiSession = useHomeChatMatchSession(user);

  const {
    wizardData,
    hasResolvedMatchIntent,
    enhancedMatchData,
    specs,
    calculateMatchPercentage,
    identityRequired,
    identityCompleteness,
    lowConfidence,
    matchLoading,
  } = useProductInsights({ product, categoryFields, aiSession });

  const { nearbyStores } = useProductContextData({
    productId: product?._id,
  });

  const {
    items: similarProductItems,
    source: similarProductsSource,
    contextSummary: similarProductsContext,
    loading: similarProductsLoading,
  } = useSimilarProducts({
    product,
    similar,
    user,
    limit: 12,
    aiSession,
  });

  const aiSessionRank = getCurrentProductAiRank(product, aiSession);
  const topAiPick = (aiSession?.products || []).reduce(
    (best, candidate, index) => {
      const parsedRank = Number(candidate?.rank);
      const rank = Number.isFinite(parsedRank) ? parsedRank : index + 1;
      if (!best || rank < best.rank) return { product: candidate, rank };
      return best;
    },
    null,
  )?.product || null;

  const currentAiEntry = aiSession?.products?.find(
    (p) => String(p._id || p.id) === String(product?._id),
  );
  const topPickScoreDiff =
    currentAiEntry?.matchScore != null && topAiPick?.matchScore != null
      ? topAiPick.matchScore - currentAiEntry.matchScore
      : null;

  const { digest: reviewDigest, loading: reviewDigestLoading } =
    useProductReviewDigest({
      productId: product?._id,
      wizardData,
      reviewCount: reviews.length,
    });

  const { summary: communitySummary, loading: communitySummaryLoading } =
    useCommunitySummary({
      productId: product?._id,
      useCase: wizardData?.useCase || null,
      refreshKey: reviews.length,
    });

  const handleAskAi = (prompt) => {
    setChatExpanded(true);
    setChatPrefill(prompt || "Is this a good fit for my needs?");
    setTimeout(() => {
      document.getElementById("product-ask-chat")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleCompareWithTopPick = () => {
    if (!product || !topAiPick) return;
    navigate("/compare", { state: { products: [product, topAiPick] } });
  };

  const similarProductsForCompare = similarProductItems.map((item) => item.product);

  const { isFavorite, handleAddToFavorites } = useProductFavoriteAction({
    product,
    trackFavorite,
    navigate,
    location,
    showToast,
  });

  // Setup Builder state
  const [showSetupModal, setShowSetupModal] = React.useState(false);
  const [setupItem, setSetupItem] = React.useState(null); // populated if already in setup

  React.useEffect(() => {
    if (!product?._id || !user) return;
    let cancelled = false;
    import("../../utils/api/axiosInstance").then(({ default: api }) => {
      api.get(`/setup/check/${product._id}`)
        .then((res) => { if (!cancelled && res.data.inSetup) setSetupItem(res.data.item); })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [product?._id, user]);

  const { trackInteraction } = useProductInteractionTracking({
    productId: product?._id,
    trackView,
  });

  const { handleCompareSimilar, handleQuickCompareFromTools } =
    useProductComparisonActions({
      product,
      alternatives: similarProductsForCompare,
      selectedProducts,
      addProduct,
      navigate,
      onTrackCompare: () => trackInteraction("compare"),
      showToast,
    });

  if (loading) {
    return <ProductDetailsPageSkeleton />;
  }
  if (error) {
    return (
      <PageStatusPanel
        title="Couldn't load this product"
        message={error}
        primaryAction={{
          label: "Try again",
          onClick: () => refetch(),
        }}
        secondaryAction={{ label: "Browse categories", to: "/" }}
      />
    );
  }
  if (!product) {
    return (
      <PageStatusPanel
        title="Product not found"
        message="This product may have been removed or the link is incorrect."
        primaryAction={{ label: "Browse categories", to: "/" }}
        secondaryAction={{ label: "Go back", onClick: () => navigate(-1) }}
        role="status"
      />
    );
  }

  const brandName =
    product?.brand && typeof product.brand === "object"
      ? product.brand.name
      : product?.brand;
  const productModel = String(product.model ?? "").trim();
  const categoryName =
    product.category && typeof product.category === "object"
      ? product.category.name
      : null;
  const showProductMeta =
    categoryName || brandName || productModel || product.year;
  const displayPrice = formatDisplayPrice(product, region);
  const hasHeroMatchScore = calculateMatchPercentage?.percentage != null;
  const productTitle =
    product.seoTitle ||
    `${product.name}${brandName ? ` - ${brandName}` : ""}`;
  const productDescription =
    product.seoDescription ||
    (product.description
      ? String(product.description).slice(0, 160)
      : `Compare ${product.name} and see why it fits you.`);
  const productCategoryId =
    product.category && typeof product.category === "object"
      ? product.category._id
      : product.category;
  const productJsonLd = [
    buildProductJsonLd(product),
    buildBreadcrumbJsonLd([
      { name: "Home", pathname: "/" },
      ...(categoryName && productCategoryId
        ? [
            {
              name: categoryName,
              pathname: `/category/${productCategoryId}`,
            },
          ]
        : []),
      { name: product.name || "Product", pathname: `/product/${id}` },
    ]),
  ];

  return (
    <div className="pageContainer">
      <PageSeo
        title={productTitle}
        description={productDescription}
        pathname={`/product/${id}`}
        imagePath={product?.images?.[0] || undefined}
        type="product"
      />
      <JsonLd data={productJsonLd} />
      <div className="innerContainer">
        <div className={styles.productDetailsContainer}>
          {product.active === false && (
            <div className="inactiveSign">This product is no longer active</div>
          )}
          <div className={`${styles.productDetailsBox} layoutShadow`}>
            <ProductActionTools
              isFavorite={isFavorite}
              onToggleFavorite={handleAddToFavorites}
              onCompare={handleQuickCompareFromTools}
              setupItem={setupItem}
              onSetupClick={() => setShowSetupModal(true)}
            />

            <ProductImageGallery
              product={product}
              selectedImageIndex={selectedImageIndex}
              imageZoom={imageZoom}
              zoomPosition={zoomPosition}
              onPrevImage={handlePreviousImage}
              onNextImage={handleNextImage}
              onOpenLightbox={() => setLightboxOpen(true)}
              onMouseEnter={() => setImageZoom(true)}
              onMouseLeave={() => setImageZoom(false)}
              onMouseMove={handleImageMouseMove}
              onSelectImage={setSelectedImageIndex}
            />

            <div className={styles.productDetailsRight}>
              <div className={styles.rightInfo}>
                <h1 className={styles.productDetailsTitle}>
                  {capitalizeFirst(product?.name) ||
                    `${capitalizeFirst(
                      product.brand?.name
                    )} ${capitalizeFirst(product.model)} `}
                </h1>
                {showProductMeta && (
                  <div className={styles.productDetailsMeta}>
                    {categoryName && (
                      <div className={styles.metaItem}>
                        <FaTag />
                        <span>
                          <b>Category:</b> {capitalizeFirst(categoryName)}
                        </span>
                      </div>
                    )}
                    {brandName && (
                      <div className={styles.metaItem}>
                        <FaIndustry />
                        <span>
                          <b>Brand:</b> {capitalizeFirst(brandName)}
                        </span>
                      </div>
                    )}
                    {productModel && (
                      <div className={styles.metaItem}>
                        <FaMicrochip />
                        <span>
                          <b>Model:</b> {capitalizeFirst(productModel)}
                        </span>
                      </div>
                    )}
                    {product.year && (
                      <div className={styles.metaItem}>
                        <FaCalendar />
                        <span>
                          <b>Year:</b> {product.year}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className={styles.productDetailsPrice}>
                  {displayPrice && (
                    <span className={styles.productDetailsPriceAmount}>
                      {displayPrice}
                      <span className={styles.productDetailsCurrency}>USD</span>
                    </span>
                  )}
                  {product.wishlistCount > 0 && (
                    <span className={styles.wishlistCountBadge} title={`${product.wishlistCount} people have this in their wishlist`}>
                      <FaBookmark size={11} /> {product.wishlistCount} want this
                    </span>
                  )}
                  {visibleRetailerOffers(product).length ? (
                    <ProductRetailerOffers product={product} />
                  ) : (
                    <ProductBuyCta product={product} />
                  )}
                </div>

                {hasHeroMatchScore ? (
                  <ProductAiVerdictCard
                    matchData={calculateMatchPercentage}
                    aiSessionRank={aiSessionRank}
                    topAiPick={topAiPick}
                    topPickScoreDiff={
                      aiSessionRank != null && aiSessionRank > 1 ? topPickScoreDiff : null
                    }
                    onCompareWithTopPick={topAiPick ? handleCompareWithTopPick : null}
                    onAskAi={() =>
                      handleAskAi("Why does this product get this match score for me?")
                    }
                  />
                ) : !matchLoading ? (
                  <ProductAiOnboardingCta
                    identityRequired={identityRequired}
                    lowConfidence={lowConfidence}
                    identityCompleteness={identityCompleteness}
                    productName={product?.name}
                    categoryName={
                      product?.category?.name || product?.category || null
                    }
                    onAskQuestion={(question) =>
                      navigate("/onboarding/shopping-profile?mode=chat", {
                        state: {
                          from: `${location.pathname}${location.search}`,
                          prefillQuestion: question,
                        },
                      })
                    }
                    onStartSearch={() =>
                      handleAskAi(
                        "I'm looking at this product — help me understand if it fits my needs.",
                      )
                    }
                  />
                ) : null}

                {!hasHeroMatchScore && (
                  <ProductMatchInsights
                    matchData={calculateMatchPercentage}
                    iconsMap={ICONS}
                    getDimensionBarClass={(score) =>
                      getDimensionBarClass(score, styles)
                    }
                    aiSessionRank={aiSessionRank}
                  />
                )}
              </div>
            </div>
          </div>

          <ProductAskChat
            product={product}
            aiSession={aiSession}
            expanded={chatExpanded}
            onExpandedChange={setChatExpanded}
            prefillPrompt={chatPrefill}
            onPrefillConsumed={() => setChatPrefill(null)}
            onSuggestedPrompt={(prompt) => setChatPrefill(prompt)}
          />

          {/* Description Section */}
          {product.description && (
            <div className={styles.fullSpecsSection}>
              <h3 className={styles.fullSpecsTitle}>Description</h3>
              <p className={styles.productDescriptionFull}>
                {product.description}
              </p>
            </div>
          )}

          {/* Full Specifications Section - Moved out of hero box */}
          {specs?.length > 0 && (
            <div className={styles.fullSpecsSection}>
              <h3 className={styles.fullSpecsTitle}>Full Specifications</h3>
              <div className={styles.specsGrid}>
                {specs.map((s, idx) => (
                  <div
                    key={`${s.key || s.label}-${idx}`}
                    className={styles.specGridItem}
                  >
                    <div className={styles.specGridLabel}>
                      {renderSpecIcon({
                        iconName: s.icon,
                        iconClass: s.iconClass,
                        styles,
                      })}
                      <span>{capitalizeFirst(s.label || s.key)}</span>
                    </div>
                    <div className={styles.specGridValue}>
                      {renderSpecValue(
                        String(s.key || s.label || "").toLowerCase() === "brand"
                          ? capitalizeLeadingLetter(s.value)
                          : s.value,
                        {
                          styles,
                          type: s.type,
                        },
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ProductDeeperInsightsSection
            styles={styles}
            wizardData={wizardData}
            specs={specs}
            hasInsightsContext={hasResolvedMatchIntent || Boolean(enhancedMatchData)}
            product={product}
            heroMatchData={hasHeroMatchScore ? calculateMatchPercentage : null}
            deeperInsightsExpanded={deeperInsightsExpanded}
            setDeeperInsightsExpanded={setDeeperInsightsExpanded}
            enhancedMatchData={enhancedMatchData}
            matchLoading={matchLoading}
            similarProductsCount={similarProductItems.length}
            heroSummaryShown={hasHeroMatchScore}
            lowConfidence={lowConfidence}
            identityCompleteness={identityCompleteness}
            categoryName={product?.category?.name || product?.category || null}
            BudgetAnalysis={BudgetAnalysis}
            DecisionConfidence={DecisionConfidence}
            MatchVisualization={MatchVisualization}
          />

          <div id="reviews" className={styles.reviewsSection}>
            <ProductCommunityOutcomes
              summary={communitySummary}
              loading={communitySummaryLoading}
            />
            <ProductReviewDigest
              digest={reviewDigest}
              loading={reviewDigestLoading}
            />
            <ProductReviewsSummaryHeader
              averageRating={averageRating}
              reviewCount={totalReviews}
              isAuthenticated={Boolean(user)}
              hasExistingReview={Boolean(userReview)}
              isReviewFormOpen={
                userReview ? editingReview === userReview._id : showReviewModal
              }
              onToggleReviewForm={() => {
                if (userReview) {
                  if (editingReview === userReview._id) cancelEditReview();
                  else startEditReview(userReview);
                  return;
                }
                setShowReviewModal((prev) => !prev);
              }}
              onLoginToReview={() =>
                navigate("/login", {
                  state: { from: location, action: "write review" },
                })
              }
            />

            {totalReviews > 0 && (
              <ReviewRatingBreakdown
                reviews={reviews.filter((review) => review.approved)}
                ratingDistribution={ratingDistribution}
              />
            )}

            <ProductReviewFormModal
              isOpen={showReviewModal}
              reviewForm={reviewForm}
              formConfig={reviewFormConfig}
              onClose={() => setShowReviewModal(false)}
              onChange={setReviewForm}
              onSubmit={handleSubmitReview}
            />

            <ProductReviewsListSection
              reviews={reviews}
              editingReview={editingReview}
              editReviewForm={editReviewForm}
              formConfig={reviewFormConfig}
              onEditFormChange={setEditReviewForm}
              onSaveEdit={handleEditReview}
              onCancelEdit={cancelEditReview}
              onHelpful={handleReviewHelpful}
              onEditStart={startEditReview}
              onDelete={(reviewId) => {
                requestDeleteReview(reviewId);
              }}
              canEditReview={isUserReview}
              onOpenAllReviews={() => setShowReviewsModal(true)}
            />
          </div>

          <ProductNearbyStores
            stores={nearbyStores}
            productPrice={product.price}
            productId={product._id}
          />

          <AdSlot slot="product" />

          {product?._id && (
            <MarketplaceListingsSection
              title="Also available used"
              queryParams={{ product: product._id, sort: "recommended" }}
              limit={4}
              widgetSource="product_details"
              viewAllLabel="Browse all marketplace listings for this item →"
            />
          )}

          {/* Product Posts Section */}
          {product && product._id && (
            <ProductPostsSection
              productId={product._id}
              product={product}
              className={styles.detailsPagePostsSection}
              gridClassName={styles.detailsPagePostsGrid}
              postCardClassName={styles.detailsPagePostCard}
              useDefaultStyles={false}
              PostCardComponent={ProductDetailsPostCard}
            />
          )}

          <ProductSimilarProductsSection
            items={similarProductItems}
            source={similarProductsSource}
            contextSummary={similarProductsContext}
            loading={similarProductsLoading}
            showCount={showCount}
            selectedProductsCount={selectedProducts.length}
            categoryId={categoryId}
            product={product}
            onCompareSelected={() => navigate("/compare")}
            onViewFullComparison={handleCompareSimilar}
          />
          {showCount < similarProductItems.length && (
            <div className="buttonShowMoreContainer">
              <button
                type="button"
                className="buttonShowMore"
                onClick={() => setShowCount((prev) => prev + 8)}
              >
                Show More
              </button>
            </div>
          )}

          <AppFeedbackPrompt
            source="product"
            prompt="How is this product page working for you?"
            metadata={{
              productId: product._id,
              productName: product.name,
              sessionId: readHomeChatMatchSessionId(),
            }}
          />

          <ProductImageLightbox
            isOpen={lightboxOpen}
            product={product}
            selectedImageIndex={selectedImageIndex}
            onClose={() => setLightboxOpen(false)}
            onPrevImage={handlePreviousImage}
            onNextImage={handleNextImage}
          />
        </div>
      </div>

      <ProductReviewsModal
        isOpen={showReviewsModal}
        reviews={reviews}
        averageRating={averageRating}
        ratingDistribution={ratingDistribution}
        approvedCount={totalReviews}
        user={user}
        onClose={() => setShowReviewsModal(false)}
        onHelpful={handleReviewHelpful}
        onLoginRequired={() =>
          navigate("/login", {
            state: { from: location, action: "mark review helpful" },
          })
        }
      />
      {showDeleteReviewModal && (
        <ConfirmModal
          message="Are you sure you want to delete this review?"
          onConfirm={confirmDeleteReview}
          onClose={cancelDeleteReview}
        />
      )}

      {showSetupModal && product && (
        <AddToSetupModal
          product={product}
          existingItem={setupItem}
          onClose={() => setShowSetupModal(false)}
          onSaved={(item) => {
            setSetupItem(item);
            setShowSetupModal(false);
          }}
          onRemoved={() => {
            setSetupItem(null);
            setShowSetupModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ProductDetailsPage;
